/**
 * Per-screen filter state — which set a surface is looking at.
 *
 * Marcus, 2026-08-27: *"Filters are one shared object — separate them. I don't
 * want a universal app filter, I want per screen filters."* The shared object
 * was the cost of making every filter apply on every screen earlier the same
 * day: a price or tag set on Discover followed you to a tier board and
 * shortened it, which reads as data loss rather than as a filter.
 *
 * So the state is six parallel sets — `fltBy`, `bTagsBy`, `xTagsBy`, all keyed
 * by `FilterKey`. Nothing is shared between them, the crew lens included.
 *
 * TWO WAYS TO REACH A SET, and picking the wrong one is the mistake this module
 * exists to make hard:
 *
 *   `fltKeyOf(s)` — "whatever screen the user is on". ONLY the shared filter
 *                   panel and its trigger may use this. They are mounted once,
 *                   globally, and genuinely cannot know their host any other
 *                   way.
 *   a literal key — everything else. A data selector knows which board it is
 *                   building, so `entriesOf(s, 'tonight')` states it and cannot
 *                   be wrong; reading "the current screen" there would make
 *                   Tonight's rows depend on where the user happens to be
 *                   standing. Discover's browse pipeline is the sharpest case:
 *                   it is async, so a fetch can land after a nav, and every
 *                   read in `slices/browse.ts` is pinned to `discover`.
 */

import { defaultFilters } from '../lib/logic.ts';
import type { BoardKey, FilterKey, Filters } from '../lib/types.ts';
import type { QdStore } from './types.ts';

export const FILTER_KEYS: readonly FilterKey[] = [
  'discover',
  'tonight',
  'interests',
  'goty',
  'playtests',
  'round',
  'backlog',
  'scores',
];

const IS_KEY = new Set<string>(FILTER_KEYS);

/** A fresh set per screen. Never one object shared six ways — see the header. */
export function emptyFilterMap(): Record<FilterKey, Filters> {
  return {
    discover: defaultFilters(),
    tonight: defaultFilters(),
    interests: defaultFilters(),
    goty: defaultFilters(),
    playtests: defaultFilters(),
    round: defaultFilters(),
    backlog: defaultFilters(),
    scores: defaultFilters(),
  };
}

export function emptyTagMap(): Record<FilterKey, number[]> {
  return { discover: [], tonight: [], interests: [], goty: [], playtests: [], round: [], backlog: [], scores: [] };
}

/**
 * The board search terms — same per-screen discipline as `emptyFilterMap`,
 * over the four keys that carry a box (`lib/types.ts#BoardKey`).
 *
 * Also the crew-switch blank: `slices/crews.ts#clearedCrewState` spreads this,
 * because every board this narrows is crew-scoped. A term typed against one
 * crew's list would otherwise survive the switch and read `0 OF 12 ON THE LIST`
 * against a list it was never asked about.
 */
export function emptyBoardQueries(): Record<BoardKey, string> {
  return { tonight: '', interests: '', goty: '', round: '' };
}

/**
 * The set the CURRENT screen owns.
 *
 * `detail` borrows its origin's, the way `scopeOf` always has: you drilled into
 * one of that screen's rows, so the back gesture returns you to a board still
 * narrowed the way you left it.
 *
 * Anything else — login, profile, crew, the wizard — has no panel to open, so
 * the answer is unreachable rather than wrong. It falls back to `discover`
 * because that is the only key with a pipeline attached, and a stray write
 * there is recoverable in one tap.
 */
export function fltKeyOf(s: Pick<QdStore, 'screen' | 'from'>): FilterKey {
  const scr = s.screen === 'detail' ? s.from : s.screen;
  return IS_KEY.has(scr as string) ? (scr as FilterKey) : 'discover';
}

/** The current screen's filters. Panel-only — data selectors name their key. */
export function fltOf(s: Pick<QdStore, 'screen' | 'from' | 'fltBy'>): Filters {
  return s.fltBy[fltKeyOf(s)];
}

export function bTagsOf(s: Pick<QdStore, 'screen' | 'from' | 'bTagsBy'>): number[] {
  return s.bTagsBy[fltKeyOf(s)];
}

export function xTagsOf(s: Pick<QdStore, 'screen' | 'from' | 'xTagsBy'>): number[] {
  return s.xTagsBy[fltKeyOf(s)];
}
